/**
 * Standardized Playback Error Framework (Sprint 6A).
 *
 * Providers convert native errors into these types so Host/UI/plugins
 * never branch on provider-specific error shapes.
 */

export var PlaybackErrorCode = Object.freeze({
  ProviderUnavailable: "ProviderUnavailable",
  PlaybackTimeout: "PlaybackTimeout",
  InvalidSource: "InvalidSource",
  UnsupportedProvider: "UnsupportedProvider",
  DRMFailure: "DRMFailure",
  NetworkFailure: "NetworkFailure",
  AuthorizationFailure: "AuthorizationFailure",
  UnknownPlaybackError: "UnknownPlaybackError",
  LoadFailed: "LoadFailed",
  MediaDecodeFailure: "MediaDecodeFailure",
});

/**
 * @param {string} code
 * @param {string} [message]
 * @param {object} [details]
 * @returns {object}
 */
export function createPlaybackError(code, message, details) {
  details = details || {};
  return {
    name: "PlaybackError",
    code: code || PlaybackErrorCode.UnknownPlaybackError,
    message: message || String(code || "Unknown playback error"),
    recoverable: !!details.recoverable,
    provider: details.provider || null,
    native: details.native || null,
    timestamp: Date.now(),
  };
}

/**
 * Map provider / media / network errors into the standard model.
 * @param {*} err
 * @param {object} [context] { providerName }
 * @returns {object} PlaybackError
 */
export function normalizePlaybackError(err, context) {
  context = context || {};
  if (err && err.name === "PlaybackError" && err.code) {
    return err;
  }

  var provider = context.providerName || (err && err.provider) || null;
  var rawCode = err && (err.code || err.errorCode || err.name);
  var message =
    (err && (err.message || err.msg)) ||
    (typeof err === "string" ? err : "Playback failed");
  var recoverable = !!(err && err.recoverable);

  if (
    rawCode === "NO_PROVIDER" ||
    rawCode === "UnsupportedProvider" ||
    /no streaming provider/i.test(message)
  ) {
    return createPlaybackError(
      PlaybackErrorCode.UnsupportedProvider,
      message,
      { recoverable: false, provider: provider, native: err }
    );
  }

  if (
    rawCode === "MEDIA_ERR_SRC_NOT_SUPPORTED" ||
    rawCode === 4 ||
    /invalid source|missing source/i.test(message)
  ) {
    return createPlaybackError(PlaybackErrorCode.InvalidSource, message, {
      recoverable: false,
      provider: provider,
      native: err,
    });
  }

  if (
    /timeout/i.test(String(rawCode)) ||
    /timeout/i.test(message) ||
    rawCode === "PlaybackTimeout"
  ) {
    return createPlaybackError(PlaybackErrorCode.PlaybackTimeout, message, {
      recoverable: true,
      provider: provider,
      native: err,
    });
  }

  if (
    /drm|eme|license/i.test(String(rawCode)) ||
    /drm|eme|license/i.test(message)
  ) {
    return createPlaybackError(PlaybackErrorCode.DRMFailure, message, {
      recoverable: false,
      provider: provider,
      native: err,
    });
  }

  if (
    /auth|403|401|forbidden|unauthorized/i.test(String(rawCode || "")) ||
    /auth|403|401|forbidden|unauthorized/i.test(message)
  ) {
    return createPlaybackError(
      PlaybackErrorCode.AuthorizationFailure,
      message,
      { recoverable: false, provider: provider, native: err }
    );
  }

  if (
    rawCode === "NETWORK_ERROR" ||
    rawCode === "networkError" ||
    rawCode === 2 ||
    /network|fetch|offline|MEDIA_ERR_NETWORK/i.test(String(rawCode || "")) ||
    /network|fetch|offline/i.test(message) ||
    (err && err.type === "networkError")
  ) {
    return createPlaybackError(PlaybackErrorCode.NetworkFailure, message, {
      recoverable: recoverable || true,
      provider: provider,
      native: err,
    });
  }

  if (
    /unavailable|iframe api|failed to load.*api/i.test(message) ||
    rawCode === "ProviderUnavailable"
  ) {
    return createPlaybackError(
      PlaybackErrorCode.ProviderUnavailable,
      message,
      { recoverable: true, provider: provider, native: err }
    );
  }

  if (
    rawCode === "MEDIA_ERR_DECODE" ||
    rawCode === 3 ||
    /decode/i.test(message)
  ) {
    return createPlaybackError(PlaybackErrorCode.MediaDecodeFailure, message, {
      recoverable: recoverable,
      provider: provider,
      native: err,
    });
  }

  if (rawCode === "LOAD_FAILED" || rawCode === "HLS_LOAD_FAILED") {
    return createPlaybackError(PlaybackErrorCode.LoadFailed, message, {
      recoverable: recoverable || true,
      provider: provider,
      native: err,
    });
  }

  return createPlaybackError(PlaybackErrorCode.UnknownPlaybackError, message, {
    recoverable: recoverable,
    provider: provider,
    native: err,
  });
}

export default {
  PlaybackErrorCode: PlaybackErrorCode,
  createPlaybackError: createPlaybackError,
  normalizePlaybackError: normalizePlaybackError,
};
